import { useNodeConnections, useReactFlow } from '@xyflow/react';
import { memo, useEffect } from 'react';
import { AudioPrimitive } from './primitive';
import { AudioTransform } from './transform';
import { useI18n } from '@/providers/i18n';
import { areNodePropsEqual } from '../are-node-props-equal';

export type AudioNodeProps = {
  type: string;
  data: {
    content?: {
      url: string;
      type: string;
    };
    generated?: {
      url: string;
      type: string;
      /** Multi-audio output (e.g. music models returning multiple tracks) */
      urls?: string[];
    };
    updatedAt?: string;
    model?: string;
    voice?: string;
    transcript?: string;
    instructions?: string;
    autoRun?: boolean;
    /** 节点视图模式：transform 或 primitive */
    viewMode?: 'transform' | 'primitive';
    /** 音频时长（秒），用于按时长计费 */
    durationSeconds?: number;
  };
  id: string;
};

export const AudioNode = memo((props: AudioNodeProps) => {
  const { t } = useI18n();
  const { updateNodeData } = useReactFlow();
  const connections = useNodeConnections({
    id: props.id,
    handleType: 'target',
  });
  const existingViewMode = (props.data as any)?.viewMode;
  
  // 一旦节点变成 transform，就永久保持 transform，不会变回 primitive
  const isTransform = existingViewMode === 'transform' || connections.length > 0;
  const Component = isTransform ? AudioTransform : AudioPrimitive;

  // 当节点首次变成 transform 时，保存 viewMode；若已有 content 且尚无 generated，则将 content 复制到 generated
  useEffect(() => {
    if (!isTransform) return;
    const content = (props.data as any)?.content;
    const hasGenerated = Boolean((props.data as any)?.generated?.url);
    if (existingViewMode !== 'transform') {
      const updates: any = { viewMode: 'transform' };
      if (content && !hasGenerated) {
        updates.generated = { ...content };
      }
      updateNodeData(props.id, updates);
    } else if (content && !hasGenerated) {
      updateNodeData(props.id, { generated: { ...content } });
    }
  }, [props.id, isTransform, existingViewMode, props.data, updateNodeData]);

  return <Component {...props} title={t('Audio Node')} />;
}, areNodePropsEqual);
AudioNode.displayName = 'AudioNode';
